import axiosInstance from '../config/axios.js';
import { downloadBlob } from '../utils/downloadBlob.js';

const API_BASE = '/company';

function _filenameFromContentDisposition(header) {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  return match ? decodeURIComponent(match[1].trim()) : null;
}

const SKIP_COMPANY_FORM_KEYS = new Set(['has_ewaybill_password']);

function appendCompanyFormValue(formData, key, value) {
  if (SKIP_COMPANY_FORM_KEYS.has(key)) return;
  if (value === null || value === undefined) return;
  if (
    (key === 'can_access' ||
      key === 'payment_gateway' ||
      key === 'email_notification_settings') &&
    typeof value === 'object'
  ) {
    formData.append(key, JSON.stringify(value));
    return;
  }
  // Booleans must stay explicit so unchecked boxes are not dropped as empty.
  if (typeof value === 'boolean') {
    formData.append(key, value ? 'true' : 'false');
    return;
  }
  formData.append(key, value);
}

const companyService = {
  // Get all companies with optional filters and pagination
  getAll: async (page = 1, perPage = 10, params = {}) => {
    const queryParams = {
      page,
      per_page: perPage,
      ...params
    };
    const response = await axiosInstance.get(API_BASE, { params: queryParams });
    return response.data;
  },

  getRecord: async (companyId) => {
    const response = await axiosInstance.get(`${API_BASE}/${companyId}`);
    return response.data;
  },

  // Get company by company_id (string ID like DIGI3094)
  getById: async (companyId) => {
    const response = await axiosInstance.get(`${API_BASE}/by-company-id/${companyId}`);
    return response.data;
  },

  // Create new company
  create: async (companyData, logo = null, sign = null) => {
    const formData = new FormData();
    
    // Append all company data
    Object.keys(companyData).forEach(key => {
      appendCompanyFormValue(formData, key, companyData[key]);
    });

    if (logo) {
      formData.append('logo', logo);
    }
    if (sign) {
      formData.append('sign', sign);
    }

    const response = await axiosInstance.post(API_BASE, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Update company
  update: async (companyId, companyData, logo = null, sign = null, options = {}) => {
    const formData = new FormData();
    
    // Append all company data
    Object.keys(companyData).forEach(key => {
      appendCompanyFormValue(formData, key, companyData[key]);
    });

    if (logo) {
      formData.append('logo', logo);
    } else if (options.removeLogo) {
      formData.append('remove_logo', 'true');
    }
    if (sign) {
      formData.append('sign', sign);
    } else if (options.removeSign) {
      formData.append('remove_sign', 'true');
    }

    const response = await axiosInstance.put(`${API_BASE}/${companyId}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Deactivate company (soft delete)
  deactivate: async (companyId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${companyId}`);
    return response.data;
  },

  // Backward-compatible alias
  delete: async (companyId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${companyId}`);
    return response.data;
  },

  requestDeleteChallenge: async (companyId) => {
    const response = await axiosInstance.post(`${API_BASE}/${companyId}/delete-challenge`);
    return response.data;
  },

  purgeCompany: async (companyId, payload) => {
    const response = await axiosInstance.post(`${API_BASE}/${companyId}/purge`, payload);
    return response.data;
  },

  importExcel: async (companyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(`${API_BASE}/${companyId}/import`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  exportExcel: async (companyId) => {
    const response = await axiosInstance.get(`${API_BASE}/${companyId}/export`, {
      responseType: 'blob',
    });
    const filename =
      _filenameFromContentDisposition(response.headers['content-disposition']) ||
      `company_${companyId}_export.xlsx`;
    await downloadBlob(
      response.data,
      filename,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
    return { filename };
  },

  // Activate company
  activate: async (companyId) => {
    const response = await axiosInstance.post(`${API_BASE}/${companyId}/activate`);
    return response.data;
  },

  // Get all companies for dropdowns (includes inactive)
  getAllForSelect: async () => {
    const response = await axiosInstance.get(API_BASE, {
      params: { page: 1, per_page: 1000 },
    });
    return response.data;
  },

  // Get active companies only
  getActiveCompanies: async () => {
    const response = await axiosInstance.get(`${API_BASE}/active`);
    return response.data;
  },

  // Get companies by status
  getByStatus: async (status) => {
    const response = await axiosInstance.get(`${API_BASE}/status/${status}`);
    return response.data;
  },

  // Search companies
  search: async (searchTerm) => {
    const response = await axiosInstance.get(`${API_BASE}/search`, {
      params: { q: searchTerm }
    });
    return response.data;
  },

  /** Outstanding subscription + WhatsApp dues invoice (standard A5 template, no GST). */
  downloadBillingInvoice: async (companyId) => {
    const response = await axiosInstance.post(
      '/subscription-payments/invoice',
      { company_id: companyId, subscription_ids: 'all' },
      { responseType: 'blob' },
    );
    const filename =
      _filenameFromContentDisposition(response.headers['content-disposition']) ||
      `invoice_${companyId}.pdf`;
    await downloadBlob(response.data, filename, 'application/pdf');
    return { filename };
  },

  /** Email outstanding dues invoice PDF to the company email. */
  sendBillingInvoiceEmail: async (companyId) => {
    const response = await axiosInstance.post('/subscription-payments/invoice/send', {
      company_id: companyId,
      subscription_ids: 'all',
    });
    return response.data;
  },
};

export default companyService;